import { get, post, Request, Response } from '@/api'
import { ProductSearchOptions, RequestEmailResponse } from '@/modules/product/types'
import { PAGE_SIZE, PageableResponse } from '@/types'
import {
  AccessoryGroup,
  BatchProductPriceUpdate,
  BatchRuntimeRequest,
  BundleReference,
  FullProduct,
  MediumProduct,
  ProductFilterResponse,
  ProductPriceUpdate,
  RuntimeRequest
} from '@/types/index'

const buildFilterTerm = (filters?: string[], category?: string): string | undefined => {
  const filterSet = new Set<string>()
  if (category) {
    filterSet.add(category)
  }
  if (filters) {
    for (const filter of filters) {
      filterSet.add(filter)
    }
  }
  return Array.from(filterSet).join(',') || undefined
}

export default {
  async loadComparableProducts(productIds: number[]): Promise<Response<FullProduct[]>> {
    return get<FullProduct[]>('/product-comparison', {
      params: {
        productIds: productIds.join(',')
      }
    })
  },
  loadProduct: (id: number): Promise<Response<FullProduct>> => {
    return get<FullProduct>('/products/' + id)
  },
  searchProducts: (
    options: ProductSearchOptions,
    page: number
  ): Promise<Response<PageableResponse<MediumProduct>>> => {
    const config: Request = {
      params: {
        keyword: options.keyword,
        filters: buildFilterTerm(options.filters, options.category),
        quick: options.instantSearch,
        page,
        size: options.size ?? PAGE_SIZE,
        sort: options.sort
      }
    }
    return get<PageableResponse<MediumProduct>>('/products', config)
  },
  getFilters: (options: ProductSearchOptions): Promise<Response<ProductFilterResponse>> => {
    const config: Request = {
      params: {
        keyword: options.keyword,
        filters: buildFilterTerm(options.filters, options.category)
      }
    }
    return get<ProductFilterResponse>('/products/filters', config)
  },
  loadAccessories: (id: number): Promise<Response<AccessoryGroup[]>> => {
    return get<AccessoryGroup[]>('/products/accessories/' + id)
  },
  loadBundleItems: (id: number): Promise<Response<BundleReference[]>> => {
    return get<BundleReference[]>('/products/bundle-items/' + id)
  },
  loadRealtimePrices: (request: RuntimeRequest): Promise<Response<ProductPriceUpdate>> => {
    return post<ProductPriceUpdate, RuntimeRequest>('/runtime/product', request)
  },
  loadRealtimePricesBatch: (
    request: BatchRuntimeRequest
  ): Promise<Response<BatchProductPriceUpdate[]>> => {
    return post<BatchProductPriceUpdate[], BatchRuntimeRequest>('/runtime/products', request)
  },
  getProductRequest: (id: number): Promise<Response<RequestEmailResponse>> => {
    return get<RequestEmailResponse>('/emails/request-product/' + id.toString())
  },
  sendProductRequest: (id: number): Promise<Response<string>> => {
    return post<string, unknown>('/emails/request-product/' + id.toString(), '')
  },
  loadDatasheet: (id: number, type: string): Promise<Response<Blob>> => {
    return get<Blob>('/products/' + id + '/datasheet/' + type, {
      responseType: 'blob'
    })
  }
}
